// ABOUTME: Pure planning for browser-bookmark imports — validate, dedupe, place.
// ABOUTME: Kept out of the component so the skip accounting is directly testable.

import { sanitizeUrl, normalizeUrlKey } from "./urlValidation";

/** Hard ceiling from firestore.rules: `data.items.size() <= 500`. */
export const MAX_DASHBOARD_ITEMS = 500;

export interface ParsedBookmark {
  href: string;
  title: string;
  folder: string;
}

export interface PlannedItem {
  type: "link";
  url: string;
  name: string;
  tags: string[];
  col: number;
  order: number;
}

export interface ImportPlan {
  items: PlannedItem[];
  skipped: {
    /** Not an http(s) URL — browser exports contain place:, javascript:, about: */
    invalid: number;
    /** Already on the dashboard, or repeated within the file. */
    duplicate: number;
    /** Would exceed what the security rules allow the document to hold. */
    overLimit: number;
  };
}

interface ExistingItem {
  type?: string;
  url?: string;
  col?: number;
  order?: number;
}

/**
 * Decides exactly what an import would write.
 *
 * The previous implementation looped `addDashboardItem` once per bookmark —
 * each one a full-document read-modify-write — with `col: 0, order: 0` for every
 * item, no URL validation, no dedupe, no limit check, and a bare `catch {}` that
 * reported "Imported 0 bookmarks!" with no reason.
 */
export function planBookmarkImport(
  bookmarks: ParsedBookmark[],
  existing: ExistingItem[],
  options: { columnCount?: number; maxItems?: number } = {}
): ImportPlan {
  const columnCount = Math.max(1, options.columnCount ?? 3);
  const maxItems = options.maxItems ?? MAX_DASHBOARD_ITEMS;

  const seen = new Set(
    existing
      .filter((i) => typeof i.url === "string" && i.url)
      .map((i) => normalizeUrlKey(i.url as string))
  );

  // Append after whatever already occupies each column rather than stacking
  // everything at (0, 0), which left imported links in arbitrary order.
  const nextOrder: number[] = Array.from({ length: columnCount }, (_, col) => {
    const inCol = existing.filter((i) => i.col === col);
    return inCol.length
      ? Math.max(...inCol.map((i) => (typeof i.order === "number" ? i.order : 0))) + 1
      : 0;
  });

  let remaining = Math.max(0, maxItems - existing.length);
  const items: PlannedItem[] = [];
  const skipped = { invalid: 0, duplicate: 0, overLimit: 0 };
  let cursor = 0;

  for (const bm of bookmarks) {
    const url = sanitizeUrl(bm.href || "");
    if (!url) {
      skipped.invalid += 1;
      continue;
    }

    const key = normalizeUrlKey(url);
    if (seen.has(key)) {
      skipped.duplicate += 1;
      continue;
    }

    if (remaining <= 0) {
      skipped.overLimit += 1;
      continue;
    }

    seen.add(key);
    remaining -= 1;

    const col = cursor % columnCount;
    cursor += 1;

    items.push({
      type: "link",
      url,
      name: (bm.title || "").trim() || url,
      tags: bm.folder ? [bm.folder.trim()] : [],
      col,
      order: nextOrder[col]++,
    });
  }

  return { items, skipped };
}

/** Human-readable summary of what an import did and did not do. */
export function describeImportResult(plan: ImportPlan): string {
  const { items, skipped } = plan;
  const parts = [`Imported ${items.length} bookmark${items.length === 1 ? "" : "s"}.`];

  const reasons: string[] = [];
  if (skipped.duplicate) reasons.push(`${skipped.duplicate} already on your dashboard`);
  if (skipped.invalid) reasons.push(`${skipped.invalid} not valid web links`);
  if (skipped.overLimit)
    reasons.push(`${skipped.overLimit} over the ${MAX_DASHBOARD_ITEMS}-item limit`);

  if (reasons.length) parts.push(`Skipped ${reasons.join(", ")}.`);
  return parts.join(" ");
}
